import { useEffect, useState } from 'react';
import { Globe2, MapPin, Search, Building2, Plus, ExternalLink, ShieldCheck } from 'lucide-react';
import { type SimState, type Point, Service } from '../domain/model';
import { autoEquippable, type CatalogEntry, type CatalogResult } from '../domain/catalog';
import { regionPresets } from '../domain/regions';
import { request } from '../api';
import type { Perform } from './ResourceManager';
import { CatalogEvidence } from './CatalogEvidence';
import ControlCenterSelector from './ControlCenterSelector';
export type ActivateCatalog = (ids: string[], equip: boolean) => Promise<boolean>;
export default function GermanyPanel({
  state,
  act,
  enabled,
  onActivate,
  onFocus,
  onGermany,
  onAreaMap,
  onSelectControlCenter,
}: {
  state: SimState;
  act: Perform;
  enabled: boolean;
  onActivate: ActivateCatalog;
  onFocus: (point: Point) => void;
  onGermany: () => void;
  onAreaMap: () => void;
  onSelectControlCenter: (id: string) => Promise<boolean>;
}) {
  const [query, setQuery] = useState(''),
    [service, setService] = useState(''),
    [federalState, setFederalState] = useState(''),
    [evidence, setEvidence] = useState(''),
    [hvo, setHvo] = useState(false),
    [nearby, setNearby] = useState(false),
    [offset, setOffset] = useState(0),
    [result, setResult] = useState<CatalogResult | null>(null),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(''),
    [selected, setSelected] = useState<string[]>([]),
    [equip, setEquip] = useState(true),
    [area, setArea] = useState(state.area),
    [preset, setPreset] = useState('');
  useEffect(() => {
    setArea(state.area);
    if (state.area.controlCenterId) setNearby(true);
  }, [
    state.area.name,
    state.area.center[0],
    state.area.center[1],
    state.area.radiusKm,
    state.area.controlCenterId,
  ]);
  useEffect(() => {
    setOffset(0);
    setSelected([]);
  }, [
    query,
    service,
    federalState,
    evidence,
    hvo,
    nearby,
    state.area.name,
    state.area.center[0],
    state.area.center[1],
    state.area.radiusKm,
    state.area.controlCenterId,
  ]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ q: query, offset: String(offset), limit: '50' });
      if (service) params.set('service', service);
      if (federalState) params.set('federalState', federalState);
      if (evidence) params.set('evidence', evidence);
      if (hvo) params.set('hvo', 'true');
      if (nearby) {
        if (state.area.controlCenterId) params.set('controlCenter', state.area.controlCenterId);
        else {
          params.set('lat', String(state.area.center[0]));
          params.set('lng', String(state.area.center[1]));
          params.set('radius', String(state.area.radiusKm));
        }
      }
      request<CatalogResult>(`catalog/search?${params}`)
        .then((data) => {
          if (!cancelled) setResult(data);
        })
        .catch((e) => {
          if (!cancelled) setError(e.message);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    query,
    service,
    federalState,
    evidence,
    hvo,
    nearby,
    offset,
    state.area.name,
    state.area.center[0],
    state.area.center[1],
    state.area.radiusKm,
    state.area.controlCenterId,
  ]);
  const imported = (e: CatalogEntry) =>
    state.stations.some((w) => w.source?.kind === e.source.kind && w.source.id === e.source.id);
  const pageAvailable = result?.entries.filter((e) => !imported(e) && autoEquippable(e)) ?? [];
  return (
    <div className="germany-panel">
      <div className="country-intro">
        <div className="country-icon">
          <Globe2 size={30} />
        </div>
        <div>
          <h2>Deutschland · Standortkatalog</h2>
          <p>Öffentlich kartierte Wachen, Rettungsdienste und Hilfsorganisationen.</p>
        </div>
        <button className="button secondary" onClick={onGermany}>
          <MapPin size={16} />
          Deutschlandkarte
        </button>
      </div>
      <div className="country-metrics">
        <div>
          <strong>{result?.metadata?.count.toLocaleString('de-DE') ?? '—'}</strong>
          <span>Standorte im Katalog</span>
        </div>
        <div>
          <strong>{result?.metadata?.hvoCount.toLocaleString('de-DE') ?? '—'}</strong>
          <span>Als HvO / First Responder erfasst</span>
        </div>
        <div>
          <strong>
            {state.stations.length}
            <em> / 1.200</em>
          </strong>
          <span>Aktive Wachen</span>
        </div>
      </div>
      {result?.metadata?.review && (
        <p className="catalog-review-summary">
          BW / Rheinland-Pfalz: {result.metadata.review.reviewed.toLocaleString('de-DE')} Standorte
          zugeordnet · {result.metadata.review.named.toLocaleString('de-DE')} Namen bereinigt ·{' '}
          {result.metadata.review.publishedFleets} Bestände mit Fahrzeugquellen. Weitere Bestände
          sind noch unbelegt.
        </p>
      )}
      <ControlCenterSelector
        state={state}
        enabled={enabled}
        onSelect={onSelectControlCenter}
        onMap={onAreaMap}
      />
      <details className="area-editor">
        <summary>
          <MapPin size={16} />
          <span>
            Eigener Übungsbereich:{' '}
            <b>{state.area.controlCenterId ? 'optional' : state.area.name}</b>
          </span>
          <span>Ändern</span>
        </summary>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const s = await act(
              {
                type: 'setArea',
                area: { name: area.name, center: area.center, radiusKm: area.radiusKm },
              },
              `Einsatzgebiet ${area.name} übernommen.`,
            );
            if (s) setNearby(true);
          }}
        >
          <p className="muted small">
            Nur für freie Szenarien ohne realen Leitstellenzuschnitt. Vorhandene Einsätze und
            Ressourcen bleiben erhalten.
          </p>
          <label>
            Region-Vorlage
            <select
              aria-label="Region-Vorlage"
              value={preset}
              onChange={(e) => {
                setPreset(e.target.value);
                const region = regionPresets.find((r) => r.name === e.target.value);
                if (region) setArea(region);
              }}
            >
              <option value="">Eigene Region / Stadt auswählen</option>
              {regionPresets.map((r) => (
                <option key={r.name}>{r.name}</option>
              ))}
            </select>
          </label>
          <div className="form-columns">
            <label>
              Gebietsname
              <input
                aria-label="Gebietsname"
                required
                minLength={2}
                maxLength={80}
                value={area.name}
                onChange={(e) => setArea({ ...area, name: e.target.value })}
              />
            </label>
            <label>
              Radius (km)
              <input
                aria-label="Gebietsradius"
                type="number"
                min={2}
                max={100}
                required
                value={area.radiusKm}
                onChange={(e) => setArea({ ...area, radiusKm: Number(e.target.value) })}
              />
            </label>
          </div>
          <div className="form-columns">
            <label>
              Breitengrad
              <input
                aria-label="Gebiet Breitengrad"
                type="number"
                step="any"
                min={47.2}
                max={55.2}
                required
                value={area.center[0]}
                onChange={(e) =>
                  setArea({ ...area, center: [Number(e.target.value), area.center[1]] })
                }
              />
            </label>
            <label>
              Längengrad
              <input
                aria-label="Gebiet Längengrad"
                type="number"
                step="any"
                min={5.8}
                max={15.2}
                required
                value={area.center[1]}
                onChange={(e) =>
                  setArea({ ...area, center: [area.center[0], Number(e.target.value)] })
                }
              />
            </label>
          </div>
          <button className="button primary" disabled={!enabled}>
            Einsatzgebiet übernehmen
          </button>
        </form>
      </details>
      <div className="catalog-filters">
        <div className="search-field">
          <Search size={16} />
          <input
            aria-label="Deutschlandkatalog durchsuchen"
            placeholder="Ort, PLZ, Wache oder Verein – z. B. DRK, DLRG, Berlin …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          aria-label="Katalog Organisation"
          value={service}
          onChange={(e) => setService(e.target.value)}
        >
          <option value="">Alle Organisationen</option>
          {Service.options.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>
      <div className="catalog-filters">
        <label>
          Bundesland
          <select
            aria-label="Katalog Bundesland"
            value={federalState}
            onChange={(e) => setFederalState(e.target.value)}
          >
            <option value="">Deutschland</option>
            <option>Baden-Württemberg</option>
            <option>Rheinland-Pfalz</option>
          </select>
        </label>
        <label>
          Fahrzeugnachweis
          <select
            aria-label="Katalog Fahrzeugnachweis"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
          >
            <option value="">Alle Bestände</option>
            <option value="published">Mit veröffentlichter Fahrzeugliste</option>
            <option value="unverified">Bestand noch unbelegt</option>
          </select>
        </label>
      </div>
      <div className="catalog-options">
        <label>
          <input type="checkbox" checked={hvo} onChange={(e) => setHvo(e.target.checked)} />
          HvO / First Responder
        </label>
        <label>
          <input type="checkbox" checked={nearby} onChange={(e) => setNearby(e.target.checked)} />
          {state.area.controlCenterId
            ? `Im Leitstellenbereich ${state.area.name.replace(/^Leitstelle\s+/i, '')}`
            : `Im Einsatzgebiet ${state.area.name}`}
        </label>
        <span>
          {loading ? 'Suche läuft …' : `${result?.total.toLocaleString('de-DE') ?? 0} Treffer`}
        </span>
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {result && !result.metadata && (
        <div className="empty">
          Noch kein Standortkatalog installiert. Den dokumentierten OSM-Import ausführen.
        </div>
      )}
      <div className="catalog-selection">
        <label>
          <input
            type="checkbox"
            aria-label="Katalogseite auswählen"
            checked={
              pageAvailable.length > 0 && pageAvailable.every((e) => selected.includes(e.id))
            }
            disabled={loading || !enabled || !pageAvailable.length}
            onChange={(e) =>
              setSelected(
                e.target.checked
                  ? [...new Set([...selected, ...pageAvailable.map((e) => e.id)])].slice(0, 100)
                  : selected.filter((id) => !pageAvailable.some((row) => row.id === id)),
              )
            }
          />
          Diese Seite auswählen
        </label>
        <span>
          OSM-Datenstand{' '}
          {result?.metadata
            ? new Date(result.metadata.dataTimestamp).toLocaleDateString('de-DE')
            : '—'}
        </span>
      </div>
      <div className="catalog-list">
        {result?.entries.map((e) => (
          <article
            className={`catalog-row ${selected.includes(e.id) ? 'selected' : ''}`}
            key={e.id}
          >
            <input
              type="checkbox"
              aria-label={`${e.name} übernehmen`}
              checked={selected.includes(e.id)}
              disabled={
                loading ||
                imported(e) ||
                !autoEquippable(e) ||
                !enabled ||
                (!selected.includes(e.id) && selected.length >= 100)
              }
              onChange={(event) =>
                setSelected(
                  event.target.checked ? [...selected, e.id] : selected.filter((id) => id !== e.id),
                )
              }
            />
            <div className="catalog-row-icon">
              <Building2 size={20} />
            </div>
            <div className="catalog-row-copy">
              <h3>{e.name}</h3>
              <p>
                {e.organization || e.stationType} · {e.service}
                {e.hvo && <b>HvO</b>}
              </p>
              <small>
                {[e.address, e.postcode, e.city].filter(Boolean).join(' · ') ||
                  `${e.location[0].toFixed(4)}, ${e.location[1].toFixed(4)}`}
              </small>
              <CatalogEvidence review={e.review} fleet={e.fleet} />
            </div>
            <div className="catalog-row-actions">
              {imported(e) ? (
                <span className="imported-label">
                  <ShieldCheck size={13} />
                  Aktiv
                </span>
              ) : null}
              <button
                aria-label={`${e.name} auf Karte`}
                title="Auf Karte zeigen"
                onClick={() => onFocus(e.location)}
              >
                <MapPin size={17} />
              </button>
              <a
                href={e.source.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`${e.name} OSM-Quelle`}
                title="OSM-Quelle öffnen"
              >
                <ExternalLink size={14} />
              </a>
            </div>
          </article>
        ))}
        {result?.metadata && !result.entries.length && !loading && (
          <div className="empty">
            Keine passenden kartierten Standorte. Filter anpassen oder eine fehlende Wache unter
            „Wachen“ selbst ergänzen.
          </div>
        )}
      </div>
      <div className="catalog-pagination">
        <button
          className="button secondary"
          disabled={offset === 0 || loading}
          onClick={() => setOffset(Math.max(0, offset - 50))}
        >
          Zurück
        </button>
        <span>
          {result?.total
            ? `${offset + 1}–${Math.min(offset + 50, result.total)} von ${result.total.toLocaleString('de-DE')}`
            : '0 Treffer'}
        </span>
        <button
          className="button secondary"
          disabled={!result || offset + 50 >= result.total || loading}
          onClick={() => setOffset(offset + 50)}
        >
          Weiter
        </button>
      </div>
      <div className="catalog-import">
        <div>
          <b>{selected.length} Standorte ausgewählt</b>
          <label>
            <input type="checkbox" checked={equip} onChange={(e) => setEquip(e.target.checked)} />
            Fahrzeuge ergänzen (Quellenbestand, sonst Simulation)
          </label>
        </div>
        <button
          className="button primary"
          disabled={loading || !enabled || !selected.length}
          onClick={async () => {
            if (await onActivate(selected, equip)) setSelected([]);
          }}
        >
          <Plus size={17} />
          In Simulation übernehmen
        </button>
      </div>
      <p className="catalog-disclaimer">
        Standorte:{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
          © OpenStreetMap-Mitwirkende · ODbL
        </a>
        . Ergänzt um einzeln verifizierte Ortsvereine. Kein vollständiges amtliches Verzeichnis;
        Funktionen können in öffentlichen Daten unvollständig sein. Bei einer Leitstellenwahl werden
        alle erfassten Standorte des Zuständigkeitsbereichs automatisch übernommen. Veröffentlichte
        Fahrzeugbestände sind einzeln belegt; fehlende Bestände und eigene Ergänzungen sind als
        Simulation gekennzeichnet. Besatzung und Verfügbarkeit bleiben simuliert. SEK/MEK werden
        ausschließlich als eigene Simulationsressourcen in der Wachenverwaltung ergänzt.
      </p>
    </div>
  );
}
